import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Search, Download,
  Code, Zap, CheckCircle, XCircle, Clock,
  Eye, Globe, Cpu, Coins, ArrowLeftRight,
  RefreshCw, Layers, Activity, Shield,
  FileText, BookOpen, Heart, GitCompare,
  PanelLeftClose, PanelLeft,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Select';
import { CategoryFilter } from '../../components/providers/CategoryFilter';
import { CategoryBadge } from '../../components/providers/CategoryBadge';
import { TemplateDocsViewerModal } from '../../components/providers/TemplateDocsViewerModal';
import { TemplateComparisonModal } from '../../components/providers/TemplateComparisonModal';
import {
  FavoriteButton,
  RecentTemplatesPanel,
  FavoritesPanel,
  FeatureFilterPanel,
  ComparisonButton,
  CopyConfigButton,
} from '../../components/providers/TemplateUXComponents';
import {
  useTemplateFavorites,
  useTemplateHistory,
  useTemplateComparison,
  useFeatureFilter,
  useDocumentationCompleteness,
} from '../../hooks/useProviderTemplateUX';
import { useProviderCategories, CategoryWithCount } from '../../hooks/useProviderCategories';
import { api } from '../../lib/api';
import { API_ENDPOINTS } from '../../config/api';

interface ProviderTemplate {
  id: string;
  name: string;
  type: string;
  category: string;
  version: string;
  status: string;
  description?: string;
  documentation?: string;
  documentationUrl?: string;
  setupGuide?: string;
  codeExamples?: Array<{
    id: string;
    language: string;
    title: string;
    description: string;
    code: string;
    operation?: string;
  }>;
  useCases?: Array<{
    id: string;
    title: string;
    description: string;
    businessScenario: string;
    recommendedFor: string[];
    icon?: string;
  }>;
  changelog?: Array<{
    version: string;
    date: string;
    changes: string[];
  }>;
  faq?: Array<{
    question: string;
    answer: string;
  }>;
  knownLimitations?: string[];
  environmentRequirements?: Array<{
    environment: 'sandbox' | 'production';
    requirements: string[];
    notes?: string;
  }>;
  config?: {
    baseUrl?: string;
    authConfig?: {
      type: string;
      headerName?: string;
    };
    credentialSchema?: Array<{ name: string; label: string; type: string; required: boolean }>;
    operations?: Array<{ name: string; method: string; endpoint: string }>;
    webhookConfig?: { enabled: boolean };
  };
  createdAt: string;
  updatedAt: string;
  instanceCount?: number;
}

const providerTypeIcons: Record<string, React.ElementType> = {
  REST: Globe,
  REST_API: Globe,
  GRAPHQL: Code,
  EVM: Cpu,
  BLOCKCHAIN_EVM: Cpu,
  SOLANA: Coins,
  BLOCKCHAIN_SOLANA: Coins,
  DEX: ArrowLeftRight,
  DEX_GENERIC: ArrowLeftRight,
};

const providerTypeLabels: Record<string, string> = {
  REST: 'REST API',
  REST_API: 'REST API',
  GRAPHQL: 'GraphQL',
  EVM: 'EVM Blockchain',
  BLOCKCHAIN_EVM: 'EVM Blockchain',
  SOLANA: 'Solana',
  BLOCKCHAIN_SOLANA: 'Solana',
  DEX: 'DEX',
  DEX_GENERIC: 'DEX',
};

export function ProviderTemplates() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<ProviderTemplate | null>(null);
  const [isDocsModalOpen, setIsDocsModalOpen] = useState(false);
  const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);

  // UX Hooks
  const { favorites, toggleFavorite, isFavorite, removeFavorite } = useTemplateFavorites();
  const { addToHistory, clearHistory, recentTemplates } = useTemplateHistory();
  const { 
    selectedTemplates: comparisonTemplates, 
    addToComparison, 
    removeFromComparison, 
    clearComparison, 
    isInComparison, 
    canAddMore,
    comparisonCount 
  } = useTemplateComparison();

  // Fetch categories for badge display
  const { data: categories } = useProviderCategories(true);
  const categoryMap = new Map(categories?.map((c: CategoryWithCount) => [c.code, c]) || []);

  // Fetch templates (only public templates for tenant admins)
  const { data: templates, isLoading, refetch } = useQuery({
    queryKey: ['provider-templates', search, typeFilter, categoryFilter, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (typeFilter) params.append('type', typeFilter);
      if (categoryFilter && categoryFilter !== 'ALL') params.append('category', categoryFilter);
      if (statusFilter) params.append('status', statusFilter);
      const response = await api.get(`${API_ENDPOINTS.providerEngine.templates}/for-tenant?${params}`);
      const result = response.data;
      return result.data || result;
    },
  });

  // Feature filter
  const { 
    filters: featureFilters, 
    filteredTemplates: featureFilteredTemplates, 
    toggleFilter: toggleFeatureFilter, 
    clearFilters: clearFeatureFilters, 
    activeFilterCount: activeFeatureFilterCount 
  } = useFeatureFilter(templates || []);

  // Apply feature filters to templates
  const displayedTemplates = useMemo(() => {
    if (activeFeatureFilterCount === 0) return templates || [];
    return featureFilteredTemplates;
  }, [templates, featureFilteredTemplates, activeFeatureFilterCount]);

  // Get templates for comparison
  const templatesForComparison = useMemo(() => {
    return (templates || []).filter((t: ProviderTemplate) => comparisonTemplates.includes(t.id));
  }, [templates, comparisonTemplates]);

  // Export template
  const handleExport = async (template: ProviderTemplate) => {
    try {
      const response = await api.get(`${API_ENDPOINTS.providerEngine.templates}/${template.id}/export`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${template.name.toLowerCase().replace(/\s+/g, '-')}-template.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // View template documentation
  const handleViewDocs = (template: ProviderTemplate) => {
    setSelectedTemplate(template);
    setIsDocsModalOpen(true);
    addToHistory(template.id, template.name);
  };

  // Handle template selection from sidebar
  const handleSelectFromSidebar = (templateId: string) => {
    const template = templates?.find((t: ProviderTemplate) => t.id === templateId);
    if (template) {
      handleViewDocs(template);
    }
  };

  // Toggle comparison for a template
  const handleToggleComparison = (templateId: string) => {
    if (isInComparison(templateId)) {
      removeFromComparison(templateId);
    } else {
      addToComparison(templateId);
    }
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
      case 'PUBLISHED':
        return <Badge variant="success"><CheckCircle className="w-3 h-3 mr-1" />Ativo</Badge>;
      case 'DRAFT':
        return <Badge variant="warning"><Clock className="w-3 h-3 mr-1" />Rascunho</Badge>;
      case 'DEPRECATED':
        return <Badge variant="error"><XCircle className="w-3 h-3 mr-1" />Deprecado</Badge>;
      case 'TESTING':
        return <Badge variant="info"><Zap className="w-3 h-3 mr-1" />Em Teste</Badge>;
      case 'VALIDATED':
        return <Badge variant="info"><CheckCircle className="w-3 h-3 mr-1" />Validado</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // Get type icon
  const getTypeIcon = (type: string) => {
    const Icon = providerTypeIcons[type] || Code;
    return <Icon className="w-5 h-5" />;
  };

  // Check if template has documentation
  const hasDocumentation = (template: ProviderTemplate) => {
    return !!(
      template.documentation ||
      template.setupGuide ||
      (template.codeExamples && template.codeExamples.length > 0) ||
      (template.useCases && template.useCases.length > 0) ||
      (template.faq && template.faq.length > 0)
    );
  };

  // Template stats
  const templateStats = {
    total: templates?.length || 0,
    active: templates?.filter((t: ProviderTemplate) => t.status === 'ACTIVE' || t.status === 'PUBLISHED').length || 0,
    withDocs: templates?.filter((t: ProviderTemplate) => hasDocumentation(t)).length || 0,
    favorites: favorites.length,
  };

  return (
    <div className="flex gap-6">
      {/* Sidebar */}
      {showSidebar && (
        <aside 
          className="w-72 flex-shrink-0 space-y-4 relative"
          aria-label="Painel de favoritos e histórico"
        >
          {/* Favorites Panel */}
          <FavoritesPanel
            favorites={favorites}
            onSelect={handleSelectFromSidebar}
            onRemove={removeFavorite}
          />

          {/* Recent Templates Panel */}
          <RecentTemplatesPanel
            history={recentTemplates}
            onSelect={handleSelectFromSidebar}
            onClear={clearHistory}
          />

          {/* Comparison Panel */}
          {comparisonCount > 0 && (
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <GitCompare className="w-4 h-4 text-purple-400" aria-hidden="true" />
                  <h4 className="text-sm font-semibold text-white">Comparação</h4>
                  <Badge className="bg-purple-500/20 text-purple-400 text-xs">{comparisonCount}/2</Badge>
                </div>
                <button 
                  onClick={clearComparison} 
                  className="text-xs text-gray-500 hover:text-gray-300"
                  aria-label="Limpar seleção de comparação"
                >
                  Limpar
                </button>
              </div>
              <div className="space-y-2 mb-3">
                {templatesForComparison.map((t: ProviderTemplate) => (
                  <div key={t.id} className="flex items-center justify-between p-2 bg-gray-800 rounded-lg">
                    <span className="text-sm text-gray-300 truncate">{t.name}</span>
                    <button
                      onClick={() => removeFromComparison(t.id)}
                      className="text-gray-500 hover:text-red-400"
                      aria-label={`Remover ${t.name} da comparação`}
                    >
                      <XCircle className="w-4 h-4" aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
              <Button
                variant="primary"
                size="sm"
                className="w-full"
                disabled={comparisonCount < 2}
                onClick={() => setIsComparisonModalOpen(true)}
                aria-label="Abrir comparação de templates"
              >
                <GitCompare className="w-4 h-4 mr-2" aria-hidden="true" />
                Comparar Templates
              </Button>
            </Card>
          )}
        </aside>
      )}

      {/* Main Content */}
      <div className="flex-1 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-3">
          {/* Sidebar Toggle Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSidebar(!showSidebar)}
            aria-label={showSidebar ? "Ocultar painel lateral" : "Mostrar painel lateral"}
            aria-expanded={showSidebar}
          >
            {showSidebar ? (
              <PanelLeftClose className="w-5 h-5" />
            ) : (
              <PanelLeft className="w-5 h-5" />
            )}
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">Provider Templates</h1>
            <p className="text-surface-400 mt-1">
              Visualize templates de integração disponíveis para seu tenant
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-500/20 rounded-lg">
              <Layers className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{templateStats.total}</p>
              <p className="text-xs text-surface-400">Templates Disponíveis</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{templateStats.active}</p>
              <p className="text-xs text-surface-400">Ativos</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <BookOpen className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{templateStats.withDocs}</p>
              <p className="text-xs text-surface-400">Com Documentação</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <Heart className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{templateStats.favorites}</p>
              <p className="text-xs text-surface-400">Favoritos</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex gap-4 mb-6">
          <div className="flex-1">
            <Input
              placeholder="Buscar templates por nome ou descrição..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <CategoryFilter
            value={categoryFilter}
            onChange={setCategoryFilter}
            includeEmpty={true}
            className="w-56"
          />
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            options={[
              { value: '', label: 'Todos os tipos' },
              { value: 'REST_API', label: 'REST API' },
              { value: 'GRAPHQL', label: 'GraphQL' },
              { value: 'BLOCKCHAIN_EVM', label: 'EVM Blockchain' },
              { value: 'BLOCKCHAIN_SOLANA', label: 'Solana' },
              { value: 'DEX_GENERIC', label: 'DEX' },
            ]}
          />
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: '', label: 'Todos os status' },
              { value: 'PUBLISHED', label: 'Publicado' },
              { value: 'VALIDATED', label: 'Validado' },
            ]}
          />
          <FeatureFilterPanel
            filters={featureFilters}
            onToggle={toggleFeatureFilter}
            onClear={clearFeatureFilters}
            activeCount={activeFeatureFilterCount}
          />
          <Button 
            variant="ghost" 
            onClick={() => refetch()}
            aria-label="Atualizar lista de templates"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Templates List */}
        {isLoading ? (
          <div className="space-y-3">
            {/* Skeleton Loaders */}
            {[1, 2, 3, 4, 5].map((i) => (
              <div 
                key={i} 
                className="flex items-center justify-between p-4 bg-surface-800/50 rounded-lg border border-surface-700 animate-pulse"
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-12 h-12 bg-surface-700 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-surface-700 rounded w-1/3" />
                    <div className="h-3 bg-surface-700 rounded w-2/3" />
                    <div className="flex gap-4 mt-2">
                      <div className="h-3 bg-surface-700 rounded w-20" />
                      <div className="h-3 bg-surface-700 rounded w-16" />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="h-6 w-16 bg-surface-700 rounded" />
                  <div className="h-6 w-16 bg-surface-700 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : !displayedTemplates?.length ? (
          <div className="text-center py-16 border-2 border-dashed border-surface-600 rounded-lg bg-surface-800/30">
            <div className="w-20 h-20 mx-auto mb-4 bg-surface-700/50 rounded-full flex items-center justify-center">
              <Layers className="w-10 h-10 text-surface-500" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">
              {activeFeatureFilterCount > 0 
                ? 'Nenhum template corresponde aos filtros'
                : 'Nenhum template disponível'}
            </h3>
            <p className="text-sm text-surface-400 mb-6 max-w-md mx-auto">
              {activeFeatureFilterCount > 0 
                ? 'Tente ajustar os filtros de recursos ou remova alguns para ver mais resultados.'
                : 'Não há templates públicos disponíveis para seu tenant no momento. Entre em contato com o administrador da plataforma.'}
            </p>
            {activeFeatureFilterCount > 0 && (
              <Button 
                variant="secondary" 
                onClick={clearFeatureFilters}
                aria-label="Limpar todos os filtros"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Limpar Filtros
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {displayedTemplates.map((template: ProviderTemplate) => (
              <TemplateRow
                key={template.id}
                template={template}
                categoryMap={categoryMap}
                isFavorite={isFavorite(template.id)}
                onToggleFavorite={() => toggleFavorite(template.id, template.name)}
                isInComparison={isInComparison(template.id)}
                canAddToComparison={canAddMore}
                onToggleComparison={() => handleToggleComparison(template.id)}
                onViewDocs={() => handleViewDocs(template)}
                onExport={() => handleExport(template)}
                hasDocumentation={hasDocumentation(template)}
                getStatusBadge={getStatusBadge}
                getTypeIcon={getTypeIcon}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Documentation Viewer Modal (Read-Only) */}
      {selectedTemplate && (
        <TemplateDocsViewerModal
          isOpen={isDocsModalOpen}
          onClose={() => {
            setIsDocsModalOpen(false);
            setSelectedTemplate(null);
          }}
          template={selectedTemplate}
        />
      )}

      {/* Comparison Modal */}
      <TemplateComparisonModal
        isOpen={isComparisonModalOpen}
        onClose={() => setIsComparisonModalOpen(false)}
        templates={templatesForComparison}
      />
      </div>
    </div>
  );
}

// Template Row Component
interface TemplateRowProps {
  template: ProviderTemplate;
  categoryMap: Map<string, CategoryWithCount>;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  isInComparison: boolean;
  canAddToComparison: boolean;
  onToggleComparison: () => void;
  onViewDocs: () => void;
  onExport: () => void;
  hasDocumentation: boolean;
  getStatusBadge: (status: string) => React.ReactNode;
  getTypeIcon: (type: string) => React.ReactNode;
}

function TemplateRow({
  template,
  categoryMap,
  isFavorite,
  onToggleFavorite,
  isInComparison,
  canAddToComparison,
  onToggleComparison,
  onViewDocs,
  onExport,
  hasDocumentation,
  getStatusBadge,
  getTypeIcon,
}: TemplateRowProps) {
  const completeness = useDocumentationCompleteness(template);

  return (
    <div className="flex items-center justify-between p-4 bg-surface-800/50 rounded-lg border border-surface-700 hover:border-primary-500/50 transition-all group">
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className="p-3 bg-primary-500/20 rounded-lg text-primary-400 group-hover:bg-primary-500/30 transition-colors">
          {getTypeIcon(template.type)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-white truncate">{template.name}</h3>
            <span className="text-xs text-surface-500 bg-surface-700 px-2 py-0.5 rounded font-mono">
              v{template.version}
            </span>
            {hasDocumentation && (
              <span className="text-xs text-blue-400 bg-blue-500/20 px-2 py-0.5 rounded flex items-center gap-1">
                <FileText className="w-3 h-3" />
                {completeness.percentage}%
              </span>
            )}
          </div>
          <p className="text-sm text-surface-400 truncate">
            {template.description || 'Sem descrição'}
          </p>
          <div className="flex items-center gap-4 mt-2 text-xs text-surface-500">
            <span className="flex items-center gap-1">
              <Code className="w-3 h-3" />
              {template.config?.operations?.length || 0} operações
            </span>
            <span className="flex items-center gap-1">
              <Shield className="w-3 h-3" />
              {template.config?.credentialSchema?.length || 0} campos
            </span>
            {template.config?.webhookConfig?.enabled && (
              <span className="flex items-center gap-1 text-green-400">
                <Activity className="w-3 h-3" />
                Webhooks
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {categoryMap.get(template.category) && (
          <CategoryBadge
            category={categoryMap.get(template.category)!}
            size="sm"
          />
        )}
        <Badge variant="info">{providerTypeLabels[template.type] || template.type}</Badge>
        {getStatusBadge(template.status)}
        
        <div className="flex gap-1">
          <FavoriteButton
            isFavorite={isFavorite}
            onToggle={onToggleFavorite}
            size="sm"
          />
          <ComparisonButton
            isInComparison={isInComparison}
            canAdd={canAddToComparison}
            onToggle={onToggleComparison}
          />
          <CopyConfigButton config={template.config} />
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onViewDocs} 
            title="Ver Documentação"
            aria-label={`Ver documentação do template ${template.name}`}
            disabled={!hasDocumentation}
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onExport} 
            title="Exportar"
            aria-label={`Exportar template ${template.name}`}
          >
            <Download className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ProviderTemplates;
